import clsx from 'clsx'
import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React from 'react'
import { Alert, Col, Row } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  FormikDateTimeField,
  FormikToggleButtonGroup,
  FormikToggleCheckbox,
} from '../../../../../../components/FormikFields'
import { Section } from '../../../../../../components/ModalSidebar'
import useAuthorization from '../../../../../../hooks/useAuthorization'
import { getEmployeeFullName } from '../../../../../../util/table.util'
import { useSchedulerActionMessageMap } from '../../../scheduler.utils'
import { FORM_FIELDS, INITIAL_VALUES, SCHEDULE_TYPES } from './fieldset'

const propTypes = {
  /**
   * If true, show schedule type toggle
   */
  showScheduleTypeToggle: PropTypes.bool,
  /**
   * If true, show separator line on last section
   */
  showSeparatorLineEnd: PropTypes.bool,
  /**
   * Indicates if employee has a task scheduled for this action already
   */
  scheduledTask: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

/**
 * The ScheduleSection works with Formik, if the form you want to schedule is not integrated with Formik just wrap the ScheduleSection in Formik and use innerRef to access its values
 * Example using a form that is not integrated with formik:
 * <Formik innerRef={formikRef} initialValues={INITIAL_VALUES} validationSchema={getValidationSchema(intl)}>
 *  <ScheduleSection />
 * </Formik>
 *
 * If your form already is wrapped with Formik remember to merge the initial values and validation schema of ScheduleSection (found in the fieldset.js) with the original form.
 *
 * Steps to add ScheduleSection to your form:
 * - Add <ScheduleSection /> component to the form
 * - Merge INITIAL_VALUES (fieldset.js) with your form's initial values
 * - Concat getValidationSchema (fieldset.js) with your form's validation schema (fields to be validated are defined in FIELDS_TO_VALIDATE)
 * - When submitting the form use toApiPayload to convert the ScheduleSection form values to the api payload values
 * - To submit use the hook useEngageTaskScheduling (found in the hooks folder) to submit the scheduled task and get controls to loading, error, success from it
 * - Remember to change the label and id of the submit button to 'Schedule ___' if scheduling, you can use the utility isScheduleSelected (fieldset.js) to define if form is being scheduled or not
 * - Also change labels in the submit screen loader to say 'Scheduling...' instead of 'Offboarding user...' for instance
 * - If your form has a review panel you can use <ScheduleReviewSection /> to show the review for the scheduling
 * - If your form has a specific submission feedback you can use <ScheduleSubmitSection /> to show the results of the scheduling
 * - Pass the scheduledTask down to this component as props so it can identify if there is a task already scheduled for it
 * - Block 'Next' button or form submission if scheduledTask is present
 */
function ScheduleSection(props) {
  const { showScheduleTypeToggle = true, showSeparatorLineEnd = true, scheduledTask = null } = props ?? {} // eslint-disable-line no-unused-vars

  const intl = useIntl()

  const { values, setFieldValue } = useFormikContext()

  const actionMessageMap = useSchedulerActionMessageMap()

  const { isAuthorizedForEngageAutopilot } = useAuthorization()
  if (!isAuthorizedForEngageAutopilot) {
    return null
  }

  if (scheduledTask) {
    return (
      <Alert variant="warning" className="d-flex align-items-center mb-4">
        <i className="fas fa-triangle-exclamation me-2" />
        <FormattedMessage
          id="ENGAGE.SCHEDULING.TASK_ALREADY_SCHEDULED"
          values={{
            action: actionMessageMap[scheduledTask.action] || scheduledTask.action,
            employee: getEmployeeFullName(scheduledTask.target_meta_data),
          }}
        />
      </Alert>
    )
  }

  return (
    <>
      {showScheduleTypeToggle && (
        <div className="mb-4">
          <FormikToggleButtonGroup
            id={FORM_FIELDS.SCHEDULE_TYPE}
            variant="engage"
            options={[
              {
                label: <ToggleButtonLabel icon="fas fa-bolt" messageId="GENERAL.NOW" />,
                value: SCHEDULE_TYPES.NOW,
              },
              {
                label: <ToggleButtonLabel icon="far fa-clock" messageId="GENERAL.SCHEDULE" />,
                value: SCHEDULE_TYPES.SCHEDULE,
              },
            ]}
            onChange={(value) => {
              // if schedule type is changing to now reset schedule fields
              if (value === SCHEDULE_TYPES.NOW) {
                setFieldValue(FORM_FIELDS.SCHEDULED_DATE, INITIAL_VALUES[FORM_FIELDS.SCHEDULED_DATE])
                setFieldValue(FORM_FIELDS.ALERT_ON_SUCCESS, INITIAL_VALUES[FORM_FIELDS.ALERT_ON_SUCCESS])
                setFieldValue(FORM_FIELDS.ALERT_ON_FAILURE, INITIAL_VALUES[FORM_FIELDS.ALERT_ON_FAILURE])
              }
            }}
          />
        </div>
      )}

      {values[FORM_FIELDS.SCHEDULE_TYPE] === SCHEDULE_TYPES.SCHEDULE && (
        <>
          <Section title="GENERAL.DATE">
            <Row>
              <Col md="6">
                <FormikDateTimeField
                  id={FORM_FIELDS.SCHEDULED_DATE}
                  info={intl.formatMessage({ id: 'ENGAGE.SCHEDULING.TASK_RUN_DESCRIPTION' })}
                  step={60 * 60} // backend runs every hour scanning for scheduled tasks (step is in seconds)
                  onChange={(value) => {
                    if (value) {
                      const dateWithNoMinutes = value.split(':')[0]
                      setFieldValue(FORM_FIELDS.SCHEDULED_DATE, `${dateWithNoMinutes}:00`)
                    }
                  }}
                  showTimezone
                />
              </Col>
            </Row>
          </Section>

          <div className={clsx(!showSeparatorLineEnd && 'mb-3')}>
            <Section title="GENERAL.NOTIFICATION" showSeparatorLine={showSeparatorLineEnd}>
              <FormikToggleCheckbox
                id={FORM_FIELDS.ALERT_ON_SUCCESS}
                label={<FormattedMessage id="ENGAGE.SCHEDULING.ALERT_ON_SUCCESS" />}
                className="checkbox-left-aligned auto-z-index pt-0"
              />

              <FormikToggleCheckbox
                id={FORM_FIELDS.ALERT_ON_FAILURE}
                label={<FormattedMessage id="ENGAGE.SCHEDULING.ALERT_ON_FAILURE" />}
                className="checkbox-left-aligned auto-z-index"
              />
            </Section>
          </div>
        </>
      )}
    </>
  )
}

ScheduleSection.propTypes = propTypes

export default ScheduleSection

// SUPPORT COMPONENTS
/* eslint-disable react/prop-types */
function ToggleButtonLabel(props) {
  const { icon, messageId } = props
  return (
    <>
      <i className={icon} />
      <FormattedMessage id={messageId} />
    </>
  )
}
